<template>
  <div class="am-credit-card">
    <div class="am-credit-card__wrapper force-status-border">
      <AmInput
        v-model="creditCard.number"
        v-mask="'#### #### #### ####'"
        :test-name="'credit_card_number'"
        class="input--custom am-credit-card__number"
        :label="$t('steps.payment.card.cardNumber')"
        placeholder="**** **** **** 1234"
        :validity-state="statusNumberValidate"
        :secondary-label="textNumberValidate"
        input-mode="tel"
        @focusout.native="validateCreditCardNumber"
      >
        <template v-slot:right>
          <AmSpin v-if="requestingInfos" />
        </template>
      </AmInput>
      <AmInput
        v-model="creditCard.shelfLife"
        v-mask="'##/##'"
        :test-name="'credit_card_shelf_life'"
        class="input--custom am-credit-card__shelf__life"
        placeholder="MM/AA"
        :validity-state="statusShelfLifeValidate"
        :label="this.shelfLifeLabel"
        :secondary-label="textShelfLifeValidate"
        input-mode="tel"
        @focusout.native="validateCreditCardShelfLife"
      />
      <AmInput
        v-model="creditCard.cvv"
        v-mask="'####'"
        :test-name="'credit_card_cvv'"
        class="input--custom am-credit-card__cvv"
        placeholder="CVV"
        :label="this.cvvLabel"
        type="tel"
        :validity-state="statusCVVValidate"
        :secondary-label="textCVVValidate"
        input-mode="tel"
        @focusout.native="validateCreditCardCVV"
      />
    </div>
  </div>
</template>

<script>
import creditCardValidator from 'card-validator'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import AmSpin from '@/components/atoms/AmSpin.vue'

export default {
  name: 'AmCreditCard',
  components: {
    AmSpin,
  },
  props: {
    selectedCardType: {
      type: String,
      required: true,
      default: '',
    },
  },
  data: () => ({
    cardTypeOptions: [
      { label: 'Crédito', value: 'credito' },
      { label: 'Débito', value: 'debito' },
    ],
    setTypeInputCVV: 'password',
    iconVisibility: 'visibility_off',
    statusNumberValidate: null,
    textNumberValidate: '',
    statusShelfLifeValidate: null,
    textShelfLifeValidate: '',
    statusCVVValidate: null,
    textCVVValidate: '',
    shelfLifeLabel: '',
    cvvLabel: '',
    creditCard: {
      number: '',
      shelfLife: '',
      cvv: '',
    },
  }),
  computed: {
    ...mapGetters('Cart', ['getHash', 'getIsInternational']),
    ...mapGetters('Card', ['isMultiplePayments', 'requestingInfos']),
    cardNumber() {
      return this.creditCard.number
    },
  },
  watch: {
    cardNumber(value) {
      if (!this.getIsInternational) return

      const newCardNumber = value.replace(/\s/g, '')
      if (newCardNumber.length > 16) return

      if (newCardNumber.length === 16) {
        this.memoizedCheckCardType(value)
      }
    },
  },
  mounted() {
    this.handleResize()
    window.addEventListener('resize', this.handleResize)
    this.memoizedCheckCardType = this.memoize(this.checkCardType)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    ...mapMutations('Card', ['SET_CARD_TYPE']),
    ...mapActions('Card', ['resetCard']),
    ...mapActions('Card', ['getCardInfos']),
    emitSelectedCardType(value) {
      this.$emit('updateCardType', value)
    },
    memoize(func) {
      let cache = {}
      return function create(...args) {
        const key = JSON.stringify(args)
        if (!cache[key]) {
          cache = {}
          cache[key] = func.apply(this, args)
        }
        return cache[key]
      }
    },
    stripAccentsAndLower(text = '') {
      return text
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/\s/g, '_')
        .toLowerCase()
    },
    async checkCardType(cardNumber) {
      const spacelessCardNumber = cardNumber.replace(/\s/g, '')
      if (spacelessCardNumber.length === 16) {
        this.resetCard()

        const bin = spacelessCardNumber.slice(0, 6)
        const result = await this.getCardInfos({ bin, cart: this.getHash })

        if (result.status === 'failed') {
          const message = result.error.response.data.data ?? result.error.response.data.message
          const keyI18nMessage = this.stripAccentsAndLower(message)

          if (keyI18nMessage !== 'error_getting_the_bin') {
            this.$AmAlert({
              type: 'error',
              description: this.$t(`cardDatas.${keyI18nMessage}`),
              autoHide: true,
            })
          } else {
            this.SET_CARD_TYPE('multiplo')
          }
        }
      }
    },
    changeToTrueOrFalse: (param) => param !== 'success',
    parseNumber: (number) => parseInt(number.replaceAll(' ', ''), 10),
    updateData() {
      const { creditCard, statusNumberValidate, statusCVVValidate, statusShelfLifeValidate } = this
      this.$emit('updateCard', {
        ...creditCard,
        number: this.parseNumber(creditCard.number),
        hasNumberError: this.changeToTrueOrFalse(statusNumberValidate),
        hasCVVError: this.changeToTrueOrFalse(statusCVVValidate),
        hasShelfLifeError: this.changeToTrueOrFalse(statusShelfLifeValidate),
      })
    },
    showOrHideCVVPassword() {
      if (this.setTypeInputCVV === 'password') {
        this.setTypeInputCVV = 'tel'
        this.iconVisibility = 'visibility'
      } else {
        this.setTypeInputCVV = 'password'
        this.iconVisibility = 'visibility_off'
      }
    },
    validateCreditCard() {
      try {
        this.validateCreditCardNumber()
        this.validateCreditCardType()
        this.validateCreditCardShelfLife()
        this.validateCreditCardCVV()

        return true
      } catch (_) {
        throw new Error(false)
      } finally {
        this.updateData()
      }
    },
    validateCreditCardType() {
      const creditCardValid = creditCardValidator.number(this.creditCard.number)
      const creditCardNumberLength = this.creditCard.number.replaceAll(' ', '').length
      if (!creditCardValid.card.lengths.includes(creditCardNumberLength)) {
        this.statusNumberValidate = 'error'
        this.textNumberValidate = this.$t('steps.validators.invalid.card.fullNumber')
        this.updateData()
        return
      }
      this.statusNumberValidate = 'success'
      this.textNumberValidate = ''
      this.updateData()
    },
    validateCreditCardNumber() {
      if (!this.creditCard.number) {
        this.statusNumberValidate = 'error'
        this.textNumberValidate = this.$t('steps.validators.required')
        this.updateData()
        return
      }

      const creditCardValid = creditCardValidator.number(this.creditCard.number)

      if (!creditCardValid.isValid) {
        this.statusNumberValidate = 'error'
        this.textNumberValidate = this.$t('steps.validators.invalid.card.invalidNumber')
        this.updateData()
        return
      }

      this.statusNumberValidate = 'success'
      this.textNumberValidate = ''
      this.updateData()
    },
    validateCreditCardShelfLife() {
      if (!this.creditCard.shelfLife) {
        this.statusShelfLifeValidate = 'error'
        this.textShelfLifeValidate = this.$t('steps.validators.required')
        this.updateData()
        return
      }

      const validDate = creditCardValidator.expirationDate(this.creditCard.shelfLife)
      if (!validDate.isValid) {
        this.statusShelfLifeValidate = 'error'
        this.textShelfLifeValidate = this.$t('steps.validators.invalid.expirationDate.invalidDate')
        this.updateData()
        return
      }

      this.statusShelfLifeValidate = 'success'
      this.textShelfLifeValidate = ''
      this.updateData()
    },
    validateCreditCardCVV() {
      if (!this.creditCard.cvv) {
        this.statusCVVValidate = 'error'
        this.textCVVValidate = this.$t('steps.validators.required')
        this.updateData()
        return
      }

      if (this.creditCard.cvv.length <= 2 || this.creditCard.cvv.length > 4) {
        this.statusCVVValidate = 'error'
        this.textCVVValidate = this.$t('steps.validators.invalid.cvv.invalidNumber')
        this.updateData()
        return
      }

      this.statusCVVValidate = 'success'
      this.textCVVValidate = ''
      this.updateData()
    },
    handleResize() {
      if (this.$mq === 'mobile') {
        this.shelfLifeLabel = this.$t('steps.payment.card.shelfLife')
        this.cvvLabel = this.$t('steps.payment.card.cvv')
        return
      }
      this.shelfLifeLabel = ''
      this.cvvLabel = ''
    },
  },
}
</script>

<style lang="scss" scoped>
.am-credit-card {
  &__card-types {
    margin: 32px 0;
    width: 100%;

    ::v-deep .mr-9 {
      margin-right: 0;
    }

    ::v-deep label {
      margin-bottom: 0;
    }

    @include mobile {
      margin-top: 32px;
      margin-bottom: 12px;
    }
  }

  &__number {
    margin-top: 0;
    width: 100%;
    ::v-deep input {
      display: flex;
      border-top-right-radius: 0px !important;
      border-bottom-right-radius: 0px !important;
    }
  }

  &__shelf__life {
    ::v-deep input {
      border-radius: 0px !important;
    }
    &:not.error {
      ::v-deep input {
        border-left: none !important;
        border-right: none !important;
      }
    }

    @include mobile {
      padding-right: 12px;
      width: 50%;
    }
  }

  &__cvv {
    ::v-deep input {
      border-top-left-radius: 0px !important;
      border-bottom-left-radius: 0px !important;
    }

    &__icon {
      cursor: pointer;
      user-select: none;
    }

    @include mobile {
      width: 50%;
    }
  }

  &__wrapper {
    display: flex;
    align-items: flex-end;
    width: 100%;

    @include mobile {
      flex-direction: row;
      flex-wrap: wrap;
    }
  }
}
</style>
